import { pickTestItemSopFields, TestItemSopFields } from 'shared/api/testItem';
import { MANAGER_TEST_ITEM_TYPE_SOP } from 'shared/api/managerTestItem';
import { resolveStatusFromDates } from './mockStore';
import { toDisplayString } from './toDisplayString';
import {
    TeachingEvaluationCampaign,
    TeachingEvaluationStatus,
    lmsVoteFormatTypeToFormat,
    lmsVoteTypeToVotingMode,
} from './types';

const isPlain = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value);

const sourceBags = (raw: unknown): Record<string, unknown>[] => {
    if (!isPlain(raw)) return [];
    const bags: Record<string, unknown>[] = [raw];
    const inner = raw.data ?? raw.Data;
    if (isPlain(inner)) bags.push(inner);
    for (const bag of [...bags]) {
        const additional = bag.additionalInfo ?? bag.AdditionalInfo;
        if (isPlain(additional)) bags.push(additional);
    }
    return bags;
};

const pickFromBags = (bags: Record<string, unknown>[], keys: string[]): unknown => {
    for (const bag of bags) {
        for (const key of keys) {
            if (key in bag && bag[key] != null) return bag[key];
        }
    }
    return undefined;
};

const pickId = (bags: Record<string, unknown>[]): string => {
    const v = pickFromBags(bags, ['id', 'Id', 'testItemId', 'TestItemId']);
    return typeof v === 'string' && v.trim() ? v.trim() : '';
};

const mergeSopFields = (bags: Record<string, unknown>[]): TestItemSopFields => {
    const empty: TestItemSopFields = {
        startDate: null,
        endDate: null,
        lmsVoteFormatType: null,
        lmsVoteType: null,
        lmsModuleId: null,
        lmsModuleIds: [],
        lmsVoteTeacherIds: [],
        lmsVoteGroupIds: [],
        lmsCustomVoteGroups: [],
    };
    return bags.reduce<TestItemSopFields>((acc, bag) => {
        const sop = pickTestItemSopFields(bag);
        return {
            startDate: acc.startDate || sop.startDate,
            endDate: acc.endDate || sop.endDate,
            lmsVoteFormatType: acc.lmsVoteFormatType ?? sop.lmsVoteFormatType,
            lmsVoteType: acc.lmsVoteType ?? sop.lmsVoteType,
            lmsModuleId: acc.lmsModuleId || sop.lmsModuleId,
            lmsModuleIds: Array.from(new Set([...acc.lmsModuleIds, ...sop.lmsModuleIds])),
            lmsVoteTeacherIds: acc.lmsVoteTeacherIds.length
                ? acc.lmsVoteTeacherIds
                : sop.lmsVoteTeacherIds,
            lmsVoteGroupIds: acc.lmsVoteGroupIds.length
                ? acc.lmsVoteGroupIds
                : sop.lmsVoteGroupIds,
            lmsCustomVoteGroups: acc.lmsCustomVoteGroups.length
                ? acc.lmsCustomVoteGroups
                : sop.lmsCustomVoteGroups,
        };
    }, empty);
};

const pickAvatarUrl = (item: Record<string, unknown>): string => {
    if (typeof item.avatarUrl === 'string' && item.avatarUrl) return item.avatarUrl;
    if (typeof item.AvatarUrl === 'string' && item.AvatarUrl) return item.AvatarUrl;
    const avatar = item.avatar ?? item.Avatar;
    if (Array.isArray(avatar) && avatar[0] && typeof avatar[0] === 'object') {
        const first = avatar[0] as Record<string, unknown>;
        if (typeof first.url === 'string') return first.url;
    }
    return '';
};

const pickTeachers = (
    bags: Record<string, unknown>[],
    sop: TestItemSopFields,
): { ids: string[]; names: string[]; avatarUrls: string[] } => {
    const ids: string[] = [...sop.lmsVoteTeacherIds];
    const names: string[] = [];
    const avatarUrls: string[] = [];

    sop.lmsCustomVoteGroups.forEach((group) => {
        group.teacherIds.forEach((id) => {
            if (id && !ids.includes(id)) ids.push(id);
        });
    });

    for (const bag of bags) {
        const rawTeachers = bag.lmsVoteTeachers ?? bag.LMSVoteTeachers ?? bag.LmsVoteTeachers;
        if (!Array.isArray(rawTeachers)) continue;
        rawTeachers.forEach((item) => {
            if (item == null || typeof item !== 'object') return;
            const t = item as Record<string, unknown>;
            const id = String(
                t.accountId ??
                    t.AccountId ??
                    t.id ??
                    t.Id ??
                    t.internalId ??
                    t.InternalId ??
                    t.designerId ??
                    t.DesignerId ??
                    '',
            ).trim();
            if (id && !ids.includes(id)) ids.push(id);
            names.push(
                toDisplayString(t.customName) ||
                    toDisplayString(t.fullName) ||
                    [toDisplayString(t.name), toDisplayString(t.surName)].filter(Boolean).join(' ') ||
                    [toDisplayString(t.firstName), toDisplayString(t.lastName)].filter(Boolean).join(' ') ||
                    id,
            );
            avatarUrls.push(pickAvatarUrl(t));
        });
    }

    if (names.length === 0) {
        ids.forEach((id) => names.push(id));
    }

    return { ids, names, avatarUrls };
};

const pickUiStatus = (
    rawStatus: unknown,
    openAt: string,
    closeAt: string,
    fallback?: TeachingEvaluationStatus,
): TeachingEvaluationStatus => {
    const n = typeof rawStatus === 'number' ? rawStatus : Number(rawStatus);
    if (n === 0) return 'draft';
    if (n === 1) {
        if (openAt && closeAt) {
            return resolveStatusFromDates(openAt, closeAt, 'active');
        }
        if (openAt) {
            const open = new Date(openAt).getTime();
            if (!Number.isNaN(open) && Date.now() < open) return 'scheduled';
        }
        return 'active';
    }
    return fallback || 'draft';
};

export const isSopListRow = (raw: unknown): boolean => {
    const bags = sourceBags(raw);
    if (!bags.length) return false;
    let sawOtherType = false;
    for (const data of bags) {
        const typeRaw = data.type ?? data.Type;
        if (typeRaw == null) continue;
        const type = typeof typeRaw === 'number' ? typeRaw : Number(typeRaw);
        if (type === MANAGER_TEST_ITEM_TYPE_SOP) return true;
        if (Number.isFinite(type)) sawOtherType = true;
    }
    if (sawOtherType) return false;
    return bags.some((data) => hasSopLmsFields(data));
};

export const hasSopLmsFields = (raw: unknown): boolean => {
    const bags = sourceBags(raw);
    if (!bags.length) return false;
    const sop = mergeSopFields(bags);
    return Boolean(
        sop.lmsModuleId ||
            sop.lmsModuleIds.length ||
            sop.startDate ||
            sop.endDate ||
            sop.lmsVoteFormatType != null ||
            sop.lmsVoteType != null,
    );
};

export const listRowNeedsSopDetail = (raw: unknown): boolean => {
    const sop = mergeSopFields(sourceBags(raw));
    return !sop.startDate || !sop.endDate || sop.lmsVoteFormatType == null || sop.lmsVoteType == null;
};

export const mapSopItemToCampaign = (
    raw: unknown,
    fallback?: TeachingEvaluationCampaign | null,
): TeachingEvaluationCampaign | null => {
    const bags = sourceBags(raw);
    if (!bags.length) return fallback ?? null;
    const sop = mergeSopFields(bags);
    const id = pickId(bags) || fallback?.id || '';
    if (!id) return fallback ?? null;

    const title =
        toDisplayString(pickFromBags(bags, ['title', 'Title'])) || fallback?.name || '';
    const { ids, names, avatarUrls } = pickTeachers(bags, sop);
    const moduleIds = Array.from(
        new Set(
            [...sop.lmsModuleIds, sop.lmsModuleId, fallback?.moduleId]
                .map((id) => String(id || '').trim())
                .filter(Boolean),
        ),
    );
    const moduleId =
        moduleIds[0] ||
        (typeof pickFromBags(bags, ['projectId', 'ProjectId']) === 'string'
            ? String(pickFromBags(bags, ['projectId', 'ProjectId']))
            : '') ||
        '';
    const moduleName =
        toDisplayString(
            pickFromBags(bags, ['projectName', 'ProjectName', 'moduleName', 'ModuleName']),
        ) ||
        fallback?.moduleName ||
        '';
    const openAt = sop.startDate || fallback?.openAt || '';
    const closeAt = sop.endDate || fallback?.closeAt || '';
    const formatRaw = pickFromBags(bags, [
        'lmsVoteFormatType',
        'LMSVoteFormatType',
        'LmsVoteFormatType',
    ]);
    const votingRaw = pickFromBags(bags, ['lmsVoteType', 'LMSVoteType', 'LmsVoteType']);

    return {
        id,
        teamId: fallback?.teamId || '',
        name: title,
        moduleId,
        moduleName,
        moduleIds: moduleIds.length ? moduleIds : moduleId ? [moduleId] : [],
        format:
            formatRaw != null || sop.lmsVoteFormatType != null
                ? lmsVoteFormatTypeToFormat(formatRaw ?? sop.lmsVoteFormatType)
                : fallback?.format || 'module',
        votingMode:
            votingRaw != null || sop.lmsVoteType != null
                ? lmsVoteTypeToVotingMode(votingRaw ?? sop.lmsVoteType)
                : fallback?.votingMode || 'general',
        groupIds: sop.lmsVoteGroupIds.length
            ? sop.lmsVoteGroupIds
            : sop.lmsCustomVoteGroups.length
              ? sop.lmsCustomVoteGroups.map((group) => group.groupId)
              : fallback?.groupIds || [],
        customVoteGroups: sop.lmsCustomVoteGroups.length
            ? sop.lmsCustomVoteGroups
            : fallback?.customVoteGroups || [],
        teacherIds: ids.length ? ids : fallback?.teacherIds || [],
        teacherNames: names.length ? names : fallback?.teacherNames || [],
        teacherAvatarUrls: avatarUrls.length ? avatarUrls : fallback?.teacherAvatarUrls,
        openAt,
        closeAt,
        status: pickUiStatus(
            pickFromBags(bags, ['status', 'Status']),
            openAt,
            closeAt,
            fallback?.status,
        ),
        createdAt: fallback?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    };
};
import { pickTestItemSopFields, TestItemSopFields } from 'shared/api/testItem';
import { MANAGER_TEST_ITEM_TYPE_SOP } from 'shared/api/managerTestItem';
import { resolveStatusFromDates } from './mockStore';
import { toDisplayString } from './toDisplayString';
import {
    TeachingEvaluationCampaign,
    TeachingEvaluationStatus,
    lmsVoteFormatTypeToFormat,
    lmsVoteTypeToVotingMode,
} from './types';

const isPlain = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value);

const sourceBags = (raw: unknown): Record<string, unknown>[] => {
    if (!isPlain(raw)) return [];
    const bags: Record<string, unknown>[] = [raw];
    const inner = raw.data ?? raw.Data;
    if (isPlain(inner)) bags.push(inner);
    for (const bag of [...bags]) {
        const additional = bag.additionalInfo ?? bag.AdditionalInfo;
        if (isPlain(additional)) bags.push(additional);
    }
    return bags;
};

const pickFromBags = (bags: Record<string, unknown>[], keys: string[]): unknown => {
    for (const bag of bags) {
        for (const key of keys) {
            if (key in bag && bag[key] != null) return bag[key];
        }
    }
    return undefined;
};

const pickId = (bags: Record<string, unknown>[]): string => {
    const v = pickFromBags(bags, ['id', 'Id', 'testItemId', 'TestItemId']);
    return typeof v === 'string' && v.trim() ? v.trim() : '';
};

const mergeSopFields = (bags: Record<string, unknown>[]): TestItemSopFields => {
    const empty: TestItemSopFields = {
        startDate: null,
        endDate: null,
        lmsVoteFormatType: null,
        lmsVoteType: null,
        lmsModuleId: null,
        lmsModuleIds: [],
        lmsVoteTeacherIds: [],
        lmsVoteGroupIds: [],
        lmsCustomVoteGroups: [],
    };
    return bags.reduce<TestItemSopFields>((acc, bag) => {
        const sop = pickTestItemSopFields(bag);
        return {
            startDate: acc.startDate || sop.startDate,
            endDate: acc.endDate || sop.endDate,
            lmsVoteFormatType: acc.lmsVoteFormatType ?? sop.lmsVoteFormatType,
            lmsVoteType: acc.lmsVoteType ?? sop.lmsVoteType,
            lmsModuleId: acc.lmsModuleId || sop.lmsModuleId,
            lmsModuleIds: Array.from(new Set([...acc.lmsModuleIds, ...sop.lmsModuleIds])),
            lmsVoteTeacherIds: acc.lmsVoteTeacherIds.length
                ? acc.lmsVoteTeacherIds
                : sop.lmsVoteTeacherIds,
            lmsVoteGroupIds: acc.lmsVoteGroupIds.length
                ? acc.lmsVoteGroupIds
                : sop.lmsVoteGroupIds,
            lmsCustomVoteGroups: acc.lmsCustomVoteGroups.length
                ? acc.lmsCustomVoteGroups
                : sop.lmsCustomVoteGroups,
        };
    }, empty);
};

const pickAvatarUrl = (item: Record<string, unknown>): string => {
    if (typeof item.avatarUrl === 'string' && item.avatarUrl) return item.avatarUrl;
    if (typeof item.AvatarUrl === 'string' && item.AvatarUrl) return item.AvatarUrl;
    const avatar = item.avatar ?? item.Avatar;
    if (Array.isArray(avatar) && avatar[0] && typeof avatar[0] === 'object') {
        const first = avatar[0] as Record<string, unknown>;
        if (typeof first.url === 'string') return first.url;
    }
    return '';
};

const pickTeachers = (
    bags: Record<string, unknown>[],
    sop: TestItemSopFields,
): { ids: string[]; names: string[]; avatarUrls: string[] } => {
    const ids: string[] = [...sop.lmsVoteTeacherIds];
    const names: string[] = [];
    const avatarUrls: string[] = [];

    sop.lmsCustomVoteGroups.forEach((group) => {
        group.teacherIds.forEach((id) => {
            if (id && !ids.includes(id)) ids.push(id);
        });
    });

    for (const bag of bags) {
        const rawTeachers = bag.lmsVoteTeachers ?? bag.LMSVoteTeachers ?? bag.LmsVoteTeachers;
        if (!Array.isArray(rawTeachers)) continue;
        rawTeachers.forEach((item) => {
            if (item == null || typeof item !== 'object') return;
            const t = item as Record<string, unknown>;
            const id = String(
                t.accountId ??
                    t.AccountId ??
                    t.id ??
                    t.Id ??
                    t.internalId ??
                    t.InternalId ??
                    t.designerId ??
                    t.DesignerId ??
                    '',
            ).trim();
            if (id && !ids.includes(id)) ids.push(id);
            names.push(
                toDisplayString(t.customName) ||
                    toDisplayString(t.fullName) ||
                    [toDisplayString(t.name), toDisplayString(t.surName)].filter(Boolean).join(' ') ||
                    [toDisplayString(t.firstName), toDisplayString(t.lastName)].filter(Boolean).join(' ') ||
                    id,
            );
            avatarUrls.push(pickAvatarUrl(t));
        });
    }

    if (names.length === 0) {
        ids.forEach((id) => names.push(id));
    }

    return { ids, names, avatarUrls };
};

const pickUiStatus = (
    rawStatus: unknown,
    openAt: string,
    closeAt: string,
    fallback?: TeachingEvaluationStatus,
): TeachingEvaluationStatus => {
    const n = typeof rawStatus === 'number' ? rawStatus : Number(rawStatus);
    if (n === 0) return 'draft';
    if (n === 1) {
        if (openAt && closeAt) {
            return resolveStatusFromDates(openAt, closeAt, 'active');
        }
        if (openAt) {
            const open = new Date(openAt).getTime();
            if (!Number.isNaN(open) && Date.now() < open) return 'scheduled';
        }
        return 'active';
    }
    return fallback || 'draft';
};

export const isSopListRow = (raw: unknown): boolean => {
    const bags = sourceBags(raw);
    if (!bags.length) return false;
    let sawOtherType = false;
    for (const data of bags) {
        const typeRaw = data.type ?? data.Type;
        if (typeRaw == null) continue;
        const type = typeof typeRaw === 'number' ? typeRaw : Number(typeRaw);
        if (type === MANAGER_TEST_ITEM_TYPE_SOP) return true;
        if (Number.isFinite(type)) sawOtherType = true;
    }
    if (sawOtherType) return false;
    return bags.some((data) => hasSopLmsFields(data));
};

export const hasSopLmsFields = (raw: unknown): boolean => {
    const bags = sourceBags(raw);
    if (!bags.length) return false;
    const sop = mergeSopFields(bags);
    return Boolean(
        sop.lmsModuleId ||
            sop.lmsModuleIds.length ||
            sop.startDate ||
            sop.endDate ||
            sop.lmsVoteFormatType != null ||
            sop.lmsVoteType != null,
    );
};

export const listRowNeedsSopDetail = (raw: unknown): boolean => {
    const sop = mergeSopFields(sourceBags(raw));
    return !sop.startDate || !sop.endDate || sop.lmsVoteFormatType == null || sop.lmsVoteType == null;
};

export const mapSopItemToCampaign = (
    raw: unknown,
    fallback?: TeachingEvaluationCampaign | null,
): TeachingEvaluationCampaign | null => {
    const bags = sourceBags(raw);
    if (!bags.length) return fallback ?? null;
    const sop = mergeSopFields(bags);
    const id = pickId(bags) || fallback?.id || '';
    if (!id) return fallback ?? null;

    const title =
        toDisplayString(pickFromBags(bags, ['title', 'Title'])) || fallback?.name || '';
    const { ids, names, avatarUrls } = pickTeachers(bags, sop);
    const moduleIds = Array.from(
        new Set(
            [...sop.lmsModuleIds, sop.lmsModuleId, fallback?.moduleId]
                .map((id) => String(id || '').trim())
                .filter(Boolean),
        ),
    );
    const moduleId =
        moduleIds[0] ||
        (typeof pickFromBags(bags, ['projectId', 'ProjectId']) === 'string'
            ? String(pickFromBags(bags, ['projectId', 'ProjectId']))
            : '') ||
        '';
    const moduleName =
        toDisplayString(
            pickFromBags(bags, ['projectName', 'ProjectName', 'moduleName', 'ModuleName']),
        ) ||
        fallback?.moduleName ||
        '';
    const openAt = sop.startDate || fallback?.openAt || '';
    const closeAt = sop.endDate || fallback?.closeAt || '';
    const formatRaw = pickFromBags(bags, [
        'lmsVoteFormatType',
        'LMSVoteFormatType',
        'LmsVoteFormatType',
    ]);
    const votingRaw = pickFromBags(bags, ['lmsVoteType', 'LMSVoteType', 'LmsVoteType']);

    return {
        id,
        teamId: fallback?.teamId || '',
        name: title,
        moduleId,
        moduleName,
        moduleIds: moduleIds.length ? moduleIds : moduleId ? [moduleId] : [],
        format:
            formatRaw != null || sop.lmsVoteFormatType != null
                ? lmsVoteFormatTypeToFormat(formatRaw ?? sop.lmsVoteFormatType)
                : fallback?.format || 'module',
        votingMode:
            votingRaw != null || sop.lmsVoteType != null
                ? lmsVoteTypeToVotingMode(votingRaw ?? sop.lmsVoteType)
                : fallback?.votingMode || 'general',
        groupIds: sop.lmsVoteGroupIds.length
            ? sop.lmsVoteGroupIds
            : sop.lmsCustomVoteGroups.length
              ? sop.lmsCustomVoteGroups.map((group) => group.groupId)
              : fallback?.groupIds || [],
        customVoteGroups: sop.lmsCustomVoteGroups.length
            ? sop.lmsCustomVoteGroups
            : fallback?.customVoteGroups || [],
        teacherIds: ids.length ? ids : fallback?.teacherIds || [],
        teacherNames: names.length ? names : fallback?.teacherNames || [],
        teacherAvatarUrls: avatarUrls.length ? avatarUrls : fallback?.teacherAvatarUrls,
        openAt,
        closeAt,
        status: pickUiStatus(
            pickFromBags(bags, ['status', 'Status']),
            openAt,
            closeAt,
            fallback?.status,
        ),
        createdAt: fallback?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    };
};
